/**
 * Product category service for API calls (product_categories table).
 * @typedef {import('../models/ProductCategory').ProductCategory} ProductCategory
 * @typedef {import('../models/ProductCategory').ProductCategoryCreate} ProductCategoryCreate
 * @typedef {import('../models/ProductCategory').ProductCategoryUpdate} ProductCategoryUpdate
 */
import axios from 'axios';
import { authHeaders } from './apiAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Get all product categories
 * @param {string} accessToken - User's access token
 * @returns {Promise<{ success: boolean, product_categories: ProductCategory[] }>}
 */
export const getProductCategories = async (accessToken) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/product-categories`, {
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get product categories');
  }
};

/**
 * Get a single product category by composite key (id, sub_category_id)
 * @param {string} accessToken - User's access token
 * @param {number} id - Category id
 * @param {number} subCategoryId - Sub-category id
 * @returns {Promise<{ success: boolean, product_category?: ProductCategory }>}
 */
export const getProductCategory = async (accessToken, id, subCategoryId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/product-categories/${id}/${subCategoryId}`,
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get product category');
  }
};

/**
 * Create a new product category
 * @param {string} accessToken - User's access token
 * @param {ProductCategoryCreate} productCategoryData
 * @returns {Promise<{ success: boolean, product_category?: ProductCategory }>}
 */
export const createProductCategory = async (accessToken, productCategoryData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/product-categories`,
      { product_category: productCategoryData },
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to create product category');
  }
};

/**
 * Update a product category by composite key
 * @param {string} accessToken - User's access token
 * @param {number} id - Category id
 * @param {number} subCategoryId - Sub-category id
 * @param {ProductCategoryUpdate} productCategoryData
 * @returns {Promise<{ success: boolean, product_category?: ProductCategory }>}
 */
export const updateProductCategory = async (
  accessToken,
  id,
  subCategoryId,
  productCategoryData
) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/product-categories/${id}/${subCategoryId}`,
      { product_category: productCategoryData },
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to update product category');
  }
};

/**
 * Delete a product category by composite key
 * @param {string} accessToken - User's access token
 * @param {number} id - Category id
 * @param {number} subCategoryId - Sub-category id
 * @returns {Promise<Object>} { success, message }
 */
export const deleteProductCategory = async (accessToken, id, subCategoryId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/product-categories/${id}/${subCategoryId}`,
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete product category');
  }
};
